import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom'; // Assuming you are using react-router-dom for URL parameters
import axios from 'axios'; // For making API calls
import {
  StarIcon as StarIconSolid,
  UserIcon,
  AcademicCapIcon,
  IdentificationIcon,
  GlobeAltIcon,
  ChatBubbleBottomCenterTextIcon,
  CalendarDaysIcon,
  SparklesIcon
} from '@heroicons/react/24/solid';
import baseUrl from '../../config';


const InstructorProfile = () => {
  const { teacherId } = useParams(); // Get teacherId from URL
  const [instructor, setInstructor] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [averageRating, setAverageRating] = useState(0);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [hasReviewed, setHasReviewed] = useState(false);
  const token = localStorage.getItem('token'); // Get token from localStorage
console.log(instructor,"instructor");

  useEffect(() => {
    const fetchTeacherProfile = async () => {
      try {
        const response = await axios.get(`${baseUrl}api/students/teacherprofile?teacherId=${teacherId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data.teacher) {
          const fetchedTeacher = response.data.teacher;
          
          setInstructor({
            id: fetchedTeacher.uniqueId || 'N/A',
            name: fetchedTeacher.name || 'Teacher',
            subject: fetchedTeacher.subject || 'General',
            tagline: fetchedTeacher.tagline || 'Inspiring Minds, Building Futures!',
            img:  `${baseUrl}${fetchedTeacher.avatar}`, 
            rating: 0, // Will be calculated from reviews
            reviewsCount: 0, // Will be calculated from reviews
            referralCode: fetchedTeacher.referralCode || 'N/A',
            languages: fetchedTeacher.languages || [],
            skills: fetchedTeacher.skills || [],
            about: fetchedTeacher.bio || 'No bio available.',
            reviews: [], // Will be set separately
          });

          const fetchedReviews = fetchedTeacher.reviews || [];
          setReviews(fetchedReviews);

          const avgRating = fetchedReviews.length > 0 ? fetchedReviews.reduce((acc, review) => acc + review.rating, 0) / fetchedReviews.length : 0;
          setAverageRating(avgRating);
          setInstructor(prev => ({ ...prev, rating: avgRating, reviewsCount: fetchedReviews.length }));

          // Check if the student has already submitted a review
          const studentId = localStorage.getItem('userId'); // Assuming userId is stored in localStorage
          const hasUserReviewed = fetchedReviews.some((review) => review.studentId === studentId); // Assuming review object has a studentId field
          setHasReviewed(hasUserReviewed);
        }
      } catch (error) {
        console.error('Error fetching teacher profile:', error);
        setInstructor(null); // Handle error by setting instructor to null
      }
    };

    fetchTeacherProfile();
  }, [teacherId, token]);

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (reviewRating === 0 || reviewComment.trim() === '') {
      alert('Please select a star rating and write a comment.');
      return;
    }

    try {
      const response = await axios.post(
        `${baseUrl}api/students/rate-teacher/${teacherId}`,
        { rating: reviewRating, review: reviewComment },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      alert(response.data.message);
      const newReviewData = response.data.review; // Assuming your API returns the newly added review
      setReviews([...reviews, newReviewData]);
      setHasReviewed(true);
      setReviewRating(0);
      setReviewComment('');

      // Recalculate average rating
      const updatedReviews = [...reviews, newReviewData];
      const avgRating = updatedReviews.length > 0 ? updatedReviews.reduce((acc, review) => acc + review.rating, 0) / updatedReviews.length : 0;
      setAverageRating(avgRating);
      setInstructor(prev => ({ ...prev, rating: avgRating, reviewsCount: updatedReviews.length }));

    } catch (error) {
      console.error('Error submitting review:', error);
      alert('Error submitting review: ' + (error.response?.data?.message || 'An unexpected error occurred.'));
    }
  };

  if (!instructor) {
    return (
      <div className="flex justify-center items-center h-screen bg-gradient-to-br from-emerald-50 to-white">
        <p className="text-xl text-gray-700">Loading instructor profile...</p>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-white-50 to-white rounded-3xl  md:p-10 shadow-xl border-b-4 border-emerald-600 max-w-7xl mx-auto  font-sans overflow-hidden">

      {/* Hero Section: Image, Name, Tagline, Rating & Message Button */}
      <div className="flex flex-col md:flex-row items-center md:items-start space-y-8 md:space-y-0 md:space-x-10 mb-10 pb-8 border-b border-emerald-100">
        <div className="relative w-40 h-40 md:w-48 md:h-48 flex-shrink-0">
          <img
            className="w-full h-full rounded-full object-cover border-4 border-emerald-400 shadow-lg transform transition-transform duration-300 hover:scale-105"
            src={instructor.img}
            alt={`${instructor.name} Profile`}
          />
          {/* Online status indicator - dynamic in a real app */}
          <span className="absolute bottom-2 right-2 w-5 h-5 bg-green-500 rounded-full border-2 border-white shadow-md animate-pulse"></span>
        </div>

        <div className="text-center md:text-left flex-grow">
          <h1 className="text-5xl font-extrabold text-gray-900 mb-2 leading-tight">{instructor.name}</h1>
          <p className="text-2xl text-emerald-700 font-bold mb-2">{instructor.subject}</p>
          <p className="text-lg text-gray-600 italic mb-5">"{instructor.tagline}"</p>

          <div className="flex items-center justify-center md:justify-start mb-6">
            <div className="flex text-yellow-400 mr-2">
              {[...Array(5)].map((_, i) => (
                <StarIconSolid key={i} className={`w-7 h-7 ${i < Math.floor(averageRating) ? 'text-yellow-400' : 'text-gray-300'}`} />
              ))}
            </div>
            <span className="text-gray-800 font-bold text-xl mr-2">
              {averageRating.toFixed(1)}
            </span>
            <span className="text-gray-600 text-lg">
              ({reviews.length} reviews)
            </span>
          </div>

          <button className="bg-emerald-600 text-white px-8 py-3 rounded-full text-xl font-semibold hover:bg-emerald-700 transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center justify-center mx-auto md:mx-0">
            <ChatBubbleBottomCenterTextIcon className="w-6 h-6 mr-3" />
            Message Instructor
          </button>
        </div>
      </div>

     
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-12">
        {/* Quick Details */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
            <IdentificationIcon className="w-7 h-7 mr-3 text-emerald-600" />
            Quick Details
          </h3>
          <ul className="space-y-4 text-lg text-gray-700">
            <li className="flex items-center">
              <span className="font-semibold min-w-[140px]">Teacher ID:</span>
              <span className="flex-grow">{instructor.id}</span>
            </li>
            <li className="flex items-center">
              <span className="font-semibold min-w-[140px]">Referral Code:</span>
              <span className="flex-grow font-mono bg-emerald-50 text-emerald-800 px-3 py-1 rounded-md text-sm border border-emerald-200">{instructor.referralCode}</span>
            </li>
            <li className="flex items-center">
              <GlobeAltIcon className="w-6 h-6 mr-2 text-blue-600 flex-shrink-0" />
              <span className="font-semibold min-w-[120px]">Languages:</span>
              <span className="flex-grow">{instructor.languages.join(', ') || 'N/A'}</span>
            </li>
            <li className="flex items-start">
              <AcademicCapIcon className="w-6 h-6 mr-2 mt-1 text-purple-600 flex-shrink-0" />
              <span className="font-semibold min-w-[120px]">Expertise:</span>
              <div className="flex flex-wrap gap-2 flex-grow">
                {instructor.skills.map(skill => (
                  <span key={skill} className="bg-blue-50 text-blue-800 text-sm font-medium px-3 py-1 rounded-full border border-blue-200">
                    {skill}
                  </span>
                ))}
              </div>
            </li>
          </ul>
        </div>

        {/* Availability */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
           <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
            <UserIcon className="w-7 h-7 mr-3 text-emerald-600" />
            About {instructor.name}
          </h3>
          <p className="text-gray-700 leading-relaxed text-lg">
            {instructor.about}
          </p>
        </div>
      </div>

      ---

      {/* Review Section */}
      <div className="mt-12">
        <h3 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
          <ChatBubbleBottomCenterTextIcon className="w-8 h-8 mr-4 text-emerald-600" />
          Student Testimonials ({reviews.length})
        </h3>
        {reviews.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
            {reviews.map((review, index) => (
              <div key={review._id || index} className="bg-gray-50 p-6 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200 relative overflow-hidden">
                <div className="flex items-center mb-3">
                  <span className="font-bold text-gray-800 text-xl mr-3">{review.author || 'Anonymous Student'}</span>
                  <div className="flex text-yellow-400">
                    {[...Array(5)].map((_, i) => (
                      <StarIconSolid key={i} className={`w-5 h-5 ${i < review.rating ? 'text-yellow-400' : 'text-gray-300'}`} />
                    ))}
                  </div>
                </div>
                <p className="text-gray-600 leading-relaxed italic mb-3">"{review.review}"</p> {/* Changed review.comment to review.review */}
                <p className="text-sm text-gray-500 text-right">
                  {review.date ? new Date(review.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}
                </p>
                {/* Decorative background element */}
                <span className="absolute -bottom-4 -right-4 text-gray-200 text-opacity-30 text-8xl font-black rotate-12 select-none pointer-events-none">"</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-600 text-lg py-6 text-center">No reviews yet. Be the first to share your valuable experience!</p>
        )}

        {/* Submit Review Form */}
        {!hasReviewed && ( // Only show the form if the user hasn't reviewed yet
          <>
            <h3 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
              <StarIconSolid className="w-8 h-8 mr-4 text-yellow-500" />
              Leave Your Feedback
            </h3>
            <form onSubmit={handleSubmitReview} className="bg-gradient-to-br from-white to-emerald-50 p-8 rounded-xl shadow-lg border border-emerald-100">
              <div className="flex items-center mb-6">
                <span className="font-medium text-gray-700 text-xl mr-5">Your Rating:</span>
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <StarIconSolid
                      key={i}
                      className={`w-10 h-10 cursor-pointer transition-transform duration-200 hover:scale-110 ${
                        i < reviewRating ? 'text-yellow-400' : 'text-gray-300 hover:text-yellow-300'
                      }`}
                      onClick={() => setReviewRating(i + 1)}
                    />
                  ))}
                </div>
              </div>
              <textarea
                className="w-full p-4 border border-gray-300 rounded-lg mb-6 focus:ring-emerald-500 focus:border-emerald-500 resize-y min-h-[140px] text-lg text-gray-800 placeholder-gray-500 shadow-sm"
                placeholder="Share your experience with Ms. Emily and help other students! (e.g., What did you learn? How was her teaching style?)"
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
              ></textarea>
              <button
                type="submit"
                className="w-full bg-emerald-600 text-white px-6 py-3.5 rounded-full text-xl font-semibold hover:bg-emerald-700 transition-all duration-300 shadow-xl transform hover:scale-[1.01]"
              >
                Submit My Review
              </button>
            </form>
          </>
        )}
        {hasReviewed && (
          <div className="bg-blue-50 p-6 rounded-lg text-blue-800 border border-blue-200 text-center text-lg shadow-sm">
            <p>Thank you for your valuable feedback! You have already submitted a review for this instructor.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default InstructorProfile;